import React, { useEffect, useRef } from 'react';

import { getBooleanFF } from '@atlaskit/platform-feature-flags';

import { isLocalStorageSupported } from '../../common/utils';
import {
	useRemoteActionsOnInterval,
	useRemoteActionsOnIntervalLegacy,
} from '../../controllers/use-remote-actions-on-interval';
import { RemoteActionsContainer } from '../../services/remote-actions-store';

import { type RemoteActionsInternalProps, type RemoteActionsProps } from './types';

export type { LifecycleEventHandler } from './types';

const RemoteActionsManagerInternal = (props: RemoteActionsInternalProps) => {
	const { onLifecycleEvent } = props;
	const result = useRemoteActionsOnInterval(props);

	useEffect(() => {
		if (onLifecycleEvent) {
			if (result?.deactivated) {
				onLifecycleEvent({ action: 'pollingStop' });
			} else {
				onLifecycleEvent({ action: 'pollingStart' });
			}
		}
	}, [result?.deactivated, onLifecycleEvent]);

	return null;
};

function RemoteActionsManagerInternalLegacy(props: RemoteActionsInternalProps) {
	useRemoteActionsOnIntervalLegacy(props);
	return null;
}

const RemoteActionsManagerV2 = (props: RemoteActionsProps) => {
	const { product, onLifecycleEvent } = props;
	const isLocalStorageSupportedRef = useRef(isLocalStorageSupported());

	useEffect(() => {
		if (!isLocalStorageSupportedRef.current) {
			if (onLifecycleEvent) {
				onLifecycleEvent({
					action: 'disabled',
					attributes: { reason: 'localStorage not supported' },
				});
			}
		}
	});

	if (!isLocalStorageSupportedRef.current) {
		return null;
	}

	return (
		<RemoteActionsContainer product={product} isGlobal>
			<RemoteActionsManagerInternal {...props} />
		</RemoteActionsContainer>
	);
};

const RemoteActionsManager = (props: RemoteActionsProps) => {
	const { product } = props;

	if (getBooleanFF('platform.remote-actions-improved-features_7ft0n_rlw3z')) {
		return <RemoteActionsManagerV2 {...props} />;
	}

	if (!isLocalStorageSupported()) {
		return null;
	}

	return (
		<RemoteActionsContainer product={product} isGlobal>
			<RemoteActionsManagerInternalLegacy {...props} />
		</RemoteActionsContainer>
	);
};

export default RemoteActionsManager;
